import { useState, useEffect } from 'react'
import { get } from '@/utils/request'

type NoticeSection = {
  tag?: string
  badge?: string
  lead?: string
  bullets?: string[]
}

type Contact = {
  role?: string
  name?: string
  wechat?: string
}

type GlobalNotice = {
  enabled?: boolean
  version?: string
  header_label?: string
  sections?: NoticeSection[]
  footer?: string
  wechat?: string
  contacts?: Contact[]
}

const WeChatTag = ({ id }: { id: string }) => (
  <span className="inline-flex items-center gap-1 rounded-md bg-amber-400/10 px-2 py-0.5 font-mono text-sm font-medium text-amber-300">
    <svg viewBox="0 0 24 24" fill="currentColor" className="h-3.5 w-3.5">
      <path d="M6.29 18.251c7.547 3.848 14.13-2.013 14.13-8.055 0-1.003-.12-1.94-.349-2.831C19.912 2.707 14.98 0 9.74 0 4.345 0 0 4.187 0 9.358c0 2.767 1.261 5.247 3.26 6.979L2.573 19.5l3.717-1.249z" />
    </svg>
    {id || 'lingtij'}
  </span>
)

// 公告文案里裸写的链接自动变成可点的链接。公告是后台纯文本编辑的，没有富文本，
// 以前写了网址也只能让用户手抄，等于白写。
const LINK_RE = /(https?:\/\/[^\s，。）】]+|(?:hy|www)\.[a-z0-9.-]+\/[^\s，。）】]*)/gi
const linkify = (text: string) =>
  text.split(LINK_RE).map((chunk, i) =>
    i % 2 === 1 ? (
      <a
        key={+i}
        href={chunk.startsWith('http') ? chunk : `https://${chunk}`}
        target="_blank"
        rel="noreferrer"
        className="text-amber-300 underline underline-offset-2"
      >
        {chunk}
      </a>
    ) : (
      <span key={+i}>{chunk}</span>
    ),
  )

// 把 "...微信 {wechat}..." 渲染成 "...微信 <WeChatTag/>..."，其余文字里的链接自动可点。
const renderWithWechat = (text: string, wechat: string) => {
  if (!text) return null
  const parts = text.split('{wechat}')
  return (
    <>
      {parts.map((part, i) => (
        <span key={+i}>
          {linkify(part)}
          {i < parts.length - 1 && <WeChatTag id={wechat} />}
        </span>
      ))}
    </>
  )
}

const renderFooter = (text: string, wechat: string) => {
  if (!text) return null
  return <p className="text-xs text-white/50">{renderWithWechat(text, wechat)}</p>
}

const AdminNotice = () => {
  const [notice, setNotice] = useState<GlobalNotice | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    get('users/settings/global_notice').then((res) => {
      if (!res?.ok) return
      const n: GlobalNotice = res.notice || {}
      if (!n.enabled || !n.sections?.length) return
      const key = `admin-notice-${n.version || 'v1'}`
      if (localStorage.getItem(key)) return
      setNotice(n)
      setVisible(true)
    })
  }, [])

  const dismiss = () => {
    if (notice) localStorage.setItem(`admin-notice-${notice.version || 'v1'}`, '1')
    setVisible(false)
  }

  if (!visible || !notice) return null

  const sections = notice.sections || []
  const wechat = notice.wechat || 'lingtij'
  const footer = notice.footer || ''
  const contacts = (notice.contacts || []).filter((c) => c?.wechat)

  return (
    <section className="px-4 sm:px-6 lg:px-12">
      <div className="mx-auto w-full max-w-6xl">
        <div className="relative overflow-hidden rounded-2xl border border-amber-400/20 bg-gradient-to-br from-amber-950/60 via-stone-900/70 to-amber-950/40 px-6 py-5 backdrop-blur-sm">
          <div className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full bg-amber-400/5 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-12 -left-12 h-40 w-40 rounded-full bg-amber-500/4 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-400/15 text-amber-300">
              <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                <path d="M11.25 4.533A9.707 9.707 0 006 3a9.735 9.735 0 00-3.25.555.75.75 0 00-.5.707v14.25a.75.75 0 001 .707A8.237 8.237 0 016 18.75c1.995 0 3.823.707 5.25 1.886V4.533zM12.75 20.636A8.214 8.214 0 0118 18.75c.966 0 1.89.166 2.75.47a.75.75 0 001-.707V4.262a.75.75 0 00-.5-.707A9.735 9.735 0 0018 3a9.707 9.707 0 00-5.25 1.533v16.103z" />
              </svg>
            </div>
            <div className="flex-1 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-widest text-amber-300/80">{notice.header_label || '平台公告'}</span>
                <span className="h-px flex-1 bg-amber-400/10" />
              </div>
              {sections.map((sec, idx) => (
                <div key={+idx}>
                  {idx > 0 && <div className="my-3 border-t border-amber-400/10" />}
                  <div className="space-y-2">
                    {(sec.tag || sec.badge) && (
                      <div className="flex items-center gap-2">
                        {sec.tag && <div className="text-xs font-medium uppercase tracking-wider text-amber-400/50">{sec.tag}</div>}
                        {sec.badge && <span className="rounded-full border border-amber-400/30 px-2 py-0.5 text-xs font-medium text-amber-300/70">{sec.badge}</span>}
                      </div>
                    )}
                    {sec.lead && <p className="text-sm leading-relaxed text-white/80">{renderWithWechat(sec.lead, wechat)}</p>}
                    {sec.bullets && sec.bullets.length > 0 && (
                      <div className="space-y-1.5 rounded-xl bg-white/5 px-3 py-2.5 text-xs text-white/70">
                        {sec.bullets.map((b, bi) => (
                          <div key={+bi} className="flex items-start gap-2">
                            <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-400/60" />
                            <span>{renderWithWechat(b, wechat)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {renderFooter(footer, wechat)}
              {contacts.length > 0 && (
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-xl border border-amber-400/15 bg-amber-400/[0.04] px-3 py-2 text-xs">
                  <span className="font-medium text-amber-300/70">联系我们</span>
                  {contacts.map((c, ci) => (
                    <span key={+ci} className="inline-flex items-center gap-1 text-white/70">
                      {c.role && <span className="text-amber-300/60">{c.role}</span>}
                      {c.name && <span>{c.name}</span>}
                      <WeChatTag id={c.wechat as string} />
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={dismiss}
              className="shrink-0 appearance-none border-0 bg-transparent text-white/25 transition-colors hover:text-white/50"
              aria-label="关闭"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

export default AdminNotice
